import {
  Component,
  signal,
  computed,
  HostListener,
  QueryList,
  ViewChildren,
  AfterViewInit,
  OnDestroy,
  OnInit,
  effect,
  inject
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {ActivatedRoute, Router} from '@angular/router';
import {
  faPlus,
  faTrash,
  faSave,
  faSync,
  faBook,
  faFire,
  faClock,
  faUtensils,
  faGripVertical
} from '@fortawesome/free-solid-svg-icons';

import {
  RecipeService,
  RecipeIngredient,
  RecipePreviewRequest,
  RecipeCreateRequest,
  RecipeDetailDto,
  RecipeStepDto
} from '../../services/recipe/recipe.service';
import {RecipeFacetsService} from '../../services/recipe/recipe-facets.service';
import {RecipeFacetsCatalogue} from '../../services/recipe/recipe-facets.models';
import {HouseholdService} from '../../services/property/household.service';
import {HouseholdWithStores} from '../../models/household';
import {ConfirmService} from '../../services/confirm/confirm.service';
import {RecipeEditorV2Component} from "../../components/recipe-editor-v2";
import {RecipeFacetEditorComponent} from '../../components/recipe-facets/recipe-facet-editor.component';
import {PageHeaderService} from '../../services/page-header/page-header.service';
import {PageAction} from '../../components/app-shell/page-action.model';
import {
  UiButtonComponent,
  UiInputComponent,
  UiSelectComponent,
  UiBadgeComponent,
  UiAlertComponent,
  UiCheckboxComponent, UiCardComponent
} from '../../shared/ui';
import {AppLogger, AppLoggerService} from "../../core/logging";

type CookDevice = 'oven' | 'pan' | 'pot' | 'grill' | 'airfryer';

type OvenMode = 'top_bottom_heat' | 'convection' | 'grill';

type GrillMode = 'direct' | 'indirect';

type RackPosition = 1 | 2 | 3 | 4 | 5;

type WaitLocation = 'room' | 'fridge';

type HeatLevel = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

// Base step type with shared fields
interface BaseStep {
  id: number;
  type: 'instruction' | 'cook' | 'wait' | 'serve';
  text?: string;
}

// Discriminated union types for each step type
interface InstructionStep extends BaseStep {
  type: 'instruction';
}

interface CookStep extends BaseStep {
  type: 'cook';
  device?: CookDevice;
  mode?: OvenMode;
  tempC?: number;
  heatLevel?: HeatLevel; // For pan and grill cooking
  timeMin?: number;
  preheat?: boolean;
  rackPosition?: RackPosition; // Only for oven
  grillMode?: GrillMode; // Only for grill
}

interface WaitStep extends BaseStep {
  type: 'wait';
  waitTimeMin?: number;
  waitLocation?: WaitLocation;
  waitCovered?: boolean;
}

interface ServeStep extends BaseStep {
  type: 'serve';
}

// Union type for all step types
type RecipeStep = InstructionStep | CookStep | WaitStep | ServeStep;

@Component({
  selector: 'app-manage-recipe',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, RecipeEditorV2Component, UiInputComponent, UiButtonComponent, UiSelectComponent, UiBadgeComponent, UiAlertComponent, UiCheckboxComponent, UiCardComponent, RecipeFacetEditorComponent],
  templateUrl: './manage-recipe.component.html',
  styleUrl: './manage-recipe.component.scss'
})
export class ManageRecipeComponent implements OnInit, AfterViewInit, OnDestroy {

  readonly log: AppLogger;

  // Font Awesome Icons
  faPlus = faPlus;
  faTrash = faTrash;
  faSave = faSave;
  faSync = faSync;
  faBook = faBook;
  faFire = faFire;
  faClock = faClock;
  faUtensils = faUtensils;
  faGripVertical = faGripVertical;

  // Recipe Meta Information
  recipeTitle = signal<string>('');
  servings = signal<number>(2);
  prepTime = signal<number>(0);
  /**
   * Read-only "on the table in" total: prep plus every cook/wait step's time.
   * The backend derives and stores this; here we mirror the same computation so
   * the editor shows a live value as the user edits prep and step times.
   */
  onTableTime = computed<number>(() => {
    const stepMinutes = this.steps().reduce((sum, s) => {
      if (s.type === 'cook') return sum + (s.timeMin ?? 0);
      if (s.type === 'wait') return sum + (s.waitTimeMin ?? 0);
      return sum;
    }, 0);
    return this.prepTime() + stepMinutes;
  });

  // Public discovery flag (replaces the old visibility model — a recipe is
  // owned by a household and editable by its members; is_public just makes it
  // readable by anyone).
  isPublic = signal<boolean>(false);

  // Classification facets (manual override). Held by KEY; labels come from the
  // catalogue. `facetsTouched` gates the PUT so we only lock/override the
  // auto-classifier when the user actually changed something.
  facetCatalogue = signal<RecipeFacetsCatalogue | null>(null);
  selectedCourse = signal<string>('');
  selectedSeasons = signal<Set<string>>(new Set<string>());
  selectedDishTypes = signal<Set<string>>(new Set<string>());
  facetsUserEdited = signal<boolean>(false);
  private facetsTouched = signal<boolean>(false);

  // Validation
  validationErrors = signal<{ title?: string }>({});
  formTouched = signal<boolean>(false);

  // Households from API
  availableHouseholds = signal<HouseholdWithStores[]>([]);

  // Steps
  steps = signal<RecipeStep[]>([]);

  // Materialized ingredients from backend
  aggregatedIngredients = signal<RecipeIngredient[]>([]);

  isSaving = signal<boolean>(false);
  isDeleting = signal<boolean>(false);

  ingredientNames = [
    // Grundzutaten
    "Mehl",
    "Weizenmehl",
    "Dinkelmehl",
    "Roggenmehl",
    "Vollkornmehl",
    "Zucker",
    "Puderzucker",
    "Brauner Zucker",
    "Vanillezucker",
    "Salz",
    "Jodsalz",
    "Hefe",
    "Trockenhefe",
    "Frischhefe",
    "Butter",
    "Margarine",
    "Milch",
    "Wasser",
    "Mineralwasser",
    "Öl",
    "Olivenöl",
    "Sonnenblumenöl",
    "Rapsöl",
    "Kokosöl",

    // Eier & Milchprodukte
    "Ei",
    "Eier",
    "Eigelb",
    "Eiweiß",
    "Sahne",
    "Schlagsahne",
    "Crème fraîche",
    "Saure Sahne",
    "Joghurt",
    "Naturjoghurt",
    "Griechischer Joghurt",
    "Quark",
    "Frischkäse",
    "Mascarpone",
    "Ricotta",
    "Käse",
    "Hartkäse",
    "Weichkäse",
    "Gouda",
    "Emmentaler",
    "Bergkäse",
    "Parmesan",
    "Mozzarella",
    "Cheddar",
    "Feta",

    // Gemüse
    "Zwiebel",
    "Zwiebeln",
    "Knoblauch",
    "Knoblauchzehe",
    "Schalotte",
    "Lauch",
    "Porree",
    "Karotte",
    "Karotten",
    "Möhre",
    "Möhren",
    "Sellerie",
    "Knollensellerie",
    "Staudensellerie",
    "Paprika",
    "Rote Paprika",
    "Grüne Paprika",
    "Gelbe Paprika",
    "Tomate",
    "Tomaten",
    "Cherrytomaten",
    "Gurke",
    "Zucchini",
    "Aubergine",
    "Brokkoli",
    "Blumenkohl",
    "Spinat",
    "Mangold",
    "Grünkohl",
    "Weißkohl",
    "Rotkohl",
    "Rosenkohl",
    "Chinakohl",
    "Kartoffel",
    "Kartoffeln",
    "Süßkartoffel",
    "Radieschen",
    "Rettich",
    "Kürbis",
    "Hokkaido",
    "Butternut",
    "Erbsen",
    "Grüne Erbsen",
    "Mais",
    "Zuckermais",
    "Bohnen",
    "Grüne Bohnen",
    "Kidneybohnen",
    "Weiße Bohnen",
    "Kichererbsen",
    "Linsen",
    "Rote Linsen",
    "Gelbe Linsen",

    // Obst
    "Apfel",
    "Äpfel",
    "Birne",
    "Banane",
    "Zitrone",
    "Limette",
    "Orange",
    "Mandarine",
    "Erdbeeren",
    "Himbeeren",
    "Blaubeeren",
    "Heidelbeeren",
    "Johannisbeeren",
    "Kirschen",
    "Pfirsich",
    "Aprikose",
    "Pflaume",
    "Zwetschge",
    "Ananas",
    "Mango",
    "Kiwi",
    "Trauben",
    "Rosinen",
    "Datteln",
    "Feigen",

    // Fleisch
    "Rindfleisch",
    "Rinderhack",
    "Hackfleisch",
    "Schweinefleisch",
    "Schweinehack",
    "Hähnchen",
    "Hähnchenbrust",
    "Hähnchenschenkel",
    "Putenfleisch",
    "Lammfleisch",
    "Kalbfleisch",
    "Bacon",
    "Speck",
    "Schinken",
    "Wiener Würstchen",
    "Bratwurst",
    "Salami",

    // Fisch & Meeresfrüchte
    "Fisch",
    "Lachs",
    "Räucherlachs",
    "Thunfisch",
    "Forelle",
    "Kabeljau",
    "Seelachs",
    "Scholle",
    "Garnelen",
    "Shrimps",
    "Krabben",
    "Muscheln",

    // Getreide & Beilagen
    "Reis",
    "Langkornreis",
    "Basmatireis",
    "Jasminreis",
    "Risottoreis",
    "Nudeln",
    "Spaghetti",
    "Penne",
    "Fusilli",
    "Lasagneplatten",
    "Gnocchi",
    "Couscous",
    "Bulgur",
    "Quinoa",
    "Haferflocken",
    "Dinkel",
    "Gerste",
    "Grieß",
    "Polenta",
    "Semmelbrösel",
    "Paniermehl",
    "Toastbrot",
    "Brot",
    "Brötchen",

    // Nüsse & Samen
    "Mandeln",
    "Haselnüsse",
    "Walnüsse",
    "Cashewkerne",
    "Erdnüsse",
    "Pistazien",
    "Sesam",
    "Sonnenblumenkerne",
    "Kürbiskerne",
    "Leinsamen",
    "Chiasamen",
    "Mohn",

    // Gewürze
    "Pfeffer",
    "Schwarzer Pfeffer",
    "Weißer Pfeffer",
    "Paprikapulver",
    "Edelsüßes Paprikapulver",
    "Rosenpaprika",
    "Chilipulver",
    "Cayennepfeffer",
    "Zimt",
    "Muskat",
    "Muskatnuss",
    "Nelken",
    "Ingwer",
    "Kurkuma",
    "Kreuzkümmel",
    "Koriander",
    "Kardamom",
    "Anis",
    "Fenchel",
    "Senf",
    "Senfkörner",

    // Kräuter
    "Petersilie",
    "Schnittlauch",
    "Basilikum",
    "Thymian",
    "Rosmarin",
    "Oregano",
    "Majoran",
    "Dill",
    "Kerbel",
    "Estragon",
    "Lorbeerblatt",
    "Lorbeerblätter",

    // Backzutaten
    "Backpulver",
    "Natron",
    "Hirschhornsalz",
    "Vanille",
    "Vanilleschote",
    "Kakaopulver",
    "Schokolade",
    "Zartbitterschokolade",
    "Vollmilchschokolade",
    "Kuvertüre",
    "Kokosraspeln",
    "Marzipan",
    "Nougat",
    "Gelatine",
    "Agavendicksaft",
    "Ahornsirup",
    "Honig",

    // Saucen & Flüssigkeiten
    "Essig",
    "Apfelessig",
    "Balsamico",
    "Sojasauce",
    "Worcestersauce",
    "Tomatenmark",
    "Passierte Tomaten",
    "Gemüsebrühe",
    "Rinderbrühe",
    "Hühnerbrühe",
    "Fond",
    "Wein",
    "Weißwein",
    "Rotwein",
    "Bier",

    // Sonstiges
    "Hefeextrakt",
    "Kapern",
    "Oliven",
    "Gewürzgurken",
    "Sauerkraut",
    "Rotkohl aus dem Glas",
    "Apfelmus",
    "Preiselbeeren",
    "Mayonnaise",
    "Ketchup",
    "Senf",
    "Remoulade"
  ];

  // Drag and drop state
  draggedStepId: number | null = null;
  dragOverStepId: number | null = null;

  // Track which wait/cook steps have text editor visible
  waitStepsWithTextEditor = new Set<number>();
  cookStepsWithTextEditor = new Set<number>();

  // Mode
  isEditMode = signal<boolean>(false);
  editingRecipeId = signal<number | null>(null);
  isLoadingRecipe = signal<boolean>(false);

  /**
   * Snapshot of the last "saved/loaded" recipe payload.
   * Used to detect unsaved changes.
   */
  private baselinePayloadJson: string | null = null;

  /**
   * Warn on browser/tab close if there are unsaved changes.
   * Note: browsers show their own generic prompt.
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (!this.hasUnsavedChanges()) return;
    event.preventDefault();
    event.returnValue = '';
  }

  /**
   * Route guard hook ({@link unsavedChangesGuard}). Runs on EVERY navigation
   * away from this page — visual back button, browser back/forward buttons, and
   * any in-app navigation — so unsaved changes can't be lost silently.
   */
  async canDeactivate(): Promise<boolean> {
    if (!this.hasUnsavedChanges()) return true;

    return this.confirm.ask({
      title: 'Discard changes?',
      message: 'You have unsaved changes. Do you want to discard them and leave this page?',
      confirmText: 'Discard',
      cancelText: 'Stay',
      confirmBtnClass: 'btn-danger'
    });
  }

  /**
   * Get count of ingredients with amount > 0
   */
  get validIngredientsCount(): number {
    return this.aggregatedIngredients().filter(i => i.quantity != null).length
  }

  private nextStepId = 7;

  @ViewChildren(RecipeEditorV2Component) private recipeEditors!: QueryList<RecipeEditorV2Component>;

  private pageHeaderService = inject(PageHeaderService);

  constructor(
    private recipeService: RecipeService,
    private recipeFacetsService: RecipeFacetsService,
    private householdService: HouseholdService,
    private route: ActivatedRoute,
    private router: Router,
    private confirm: ConfirmService,
    private appLogger: AppLoggerService
  ) {
    this.log = this.appLogger.createForClass(ManageRecipeComponent);

    // Auto-refresh the page header actions whenever a flag that drives their
    // disabled/loading/visible state changes.
    effect(() => {
      // Read the signals so the effect re-runs on change.
      this.isSaving();
      this.isDeleting();
      this.isEditMode();
      this.refreshPageActions();
    });
  }

  ngAfterViewInit(): void {
    // Initial publish (covers the case where no flag changes happen before the
    // first render but we still want the toolbar populated).
    this.refreshPageActions();

    // Back button lives in the global sub-header.
    // Use the existing onBackClick() logic (handles unsaved changes + correct target).
    this.pageHeaderService.setBackButton({
      label: 'Back',
      display: 'auto',
      onClick: () => {
        void this.onBackClick();
      }
    });
  }

  /**
   * Declarative toolbar actions:
   *  - Save: primary, pinned (always visible — toolbar button on desktop,
   *    full-width second row on mobile). Disabled while saving/deleting,
   *    shows a loading spinner while saving.
   *  - Delete: default secondary action — inline on desktop, auto-collapses
   *    into the ⋯ overflow on mobile. Danger styling. Only published while
   *    we're in edit mode (a brand-new draft has nothing to delete).
   */
  private refreshPageActions(): void {
    const saving = this.isSaving();
    const deleting = this.isDeleting();
    const editMode = this.isEditMode();

    const actions: PageAction[] = [
      {
        type: 'button',
        id: 'save',
        label: 'Save',
        icon: this.faSave,
        variant: 'primary',
        pinned: true,
        disabled: saving || deleting,
        loading: saving,
        onClick: () => this.saveRecipe(),
      },
    ];

    if (editMode) {
      actions.push({
        type: 'button',
        id: 'delete',
        label: 'Delete',
        icon: this.faTrash,
        variant: 'danger',
        disabled: saving || deleting,
        loading: deleting,
        onClick: () => this.deleteRecipe(),
      });
    }

    this.pageHeaderService.setPageActions(actions);
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearAll();
  }

  /**
   * Get the breadcrumb label for the current recipe
   */
  getBreadcrumbLabel(): string {
    if (this.isEditMode() && this.editingRecipeId()) {
      return `Edit Recipe: ${this.recipeTitle()}`;
    }
    return 'New Recipe';
  }

  /**
   * Lifecycle hook - called when the component is initialized
   */
  ngOnInit(): void {
    // Facet catalogue drives the course/season/dish-type override controls.
    this.recipeFacetsService.getCatalogue().subscribe({
      next: (cat) => this.facetCatalogue.set(cat),
      error: (err) => this.log.error('Error loading recipe facet catalogue', err),
    });

    // Detect edit mode from route (/recipe/:id/edit)
    const idParam = this.route.snapshot.paramMap.get('id');
    const editSegment = this.route.snapshot.url?.some(u => u.path === 'edit');



    if (editSegment && idParam) {
      const id = Number(idParam);
      if (Number.isFinite(id)) {
        this.isEditMode.set(true);
        this.editingRecipeId.set(id);
        this.loadRecipeForEdit(id);
      }
    } else {
      this.pageHeaderService.setPageTitle("New Recipe");
    }

    // For "new recipe" mode: establish baseline after initial defaults are ready.
    // Use a microtask so any initial signal setup is applied first.
    queueMicrotask(() => this.resetBaseline());
  }

  private loadRecipeForEdit(id: number): void {
    this.isLoadingRecipe.set(true);

    this.recipeService.getRecipeById(id).subscribe({
      next: (dto) => {
        this.applyRecipeDto(dto);
        this.isLoadingRecipe.set(false);

        // After all recipe data is applied (steps/servings/etc.), immediately refresh ingredients preview.
        queueMicrotask(() => this.previewIngredients());
      },
      error: () => {
        this.isLoadingRecipe.set(false);
      }
    });
  }

  private applyRecipeDto(dto: RecipeDetailDto): void {
    this.recipeTitle.set(dto.title ?? '');
    this.servings.set(dto.servings ?? 1);
    this.prepTime.set(dto.prep_time_minutes ?? 0);

    this.pageHeaderService.setPageTitle("Edit '" + this.recipeTitle() + "'");

    // Reset validation state
    this.formTouched.set(false);
    this.validationErrors.set({});

    // public discovery flag
    this.isPublic.set(dto.is_public ?? false);

    // facets (manual override state seeded from the current classification)
    const facets = dto.facets;
    this.selectedCourse.set(facets?.course?.key ?? '');
    this.selectedSeasons.set(new Set((facets?.seasons ?? []).map(s => s.key)));
    this.selectedDishTypes.set(new Set(facets?.dish_types ?? []));
    this.facetsUserEdited.set(facets?.user_edited ?? false);
    this.facetsTouched.set(false);

    // steps
    const mappedSteps = (dto.steps ?? [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((s) => this.mapStepDtoToEditorStep(s));

    this.steps.set(mappedSteps.length ? mappedSteps : [{id: 1, type: 'instruction', text: ''}]);

    this.nextStepId = (Math.max(...this.steps().map(s => s.id), 0) || 0) + 1;

    // initial ingredients preview
    this.aggregatedIngredients.set(dto.aggregated_ingredients ?? []);

    // mark loaded state as baseline (not dirty)
    this.resetBaseline();
  }

  private mapStepDtoToEditorStep(s: RecipeStepDto): RecipeStep {
    const base = {
      id: s.order ?? this.nextStepId++,
      type: (s.type as any) ?? 'instruction',
      text: s.text ?? ''
    } as RecipeStep;

    if (s.type === 'instruction') {
      return {...base, type: 'instruction'};
    }

    if (s.type === 'serve') {
      return {...base, type: 'serve'};
    }

    if (s.type === 'cook') {
      return {
        ...base,
        type: 'cook',
        device: (s.device as CookDevice) ?? 'oven',
        mode: (s.mode as OvenMode) ?? 'top_bottom_heat',
        tempC: s.temp_c ?? 180,
        heatLevel: (s.heat_level as HeatLevel) ?? undefined,
        timeMin: s.time_min ?? 30,
        preheat: s.preheat ?? false,
        rackPosition: (s.rack_position as RackPosition) ?? 3,
        grillMode: (s.grill_mode as GrillMode) ?? undefined
      };
    }

    if (s.type === 'wait') {
      return {
        ...base,
        type: 'wait',
        waitTimeMin: s.time_min ?? 30,
        waitLocation: (s.location as WaitLocation) ?? 'room',
        waitCovered: s.covered ?? false
      };
    }

    return {id: base.id, type: 'instruction', text: base.text ?? ''};
  }

  /**
   * Helper to cast number to RackPosition
   */
  asRackPosition(position: number): RackPosition {
    return position as RackPosition;
  }

  /**
   * Helper to cast number to HeatLevel
   */
  asHeatLevel(level: number): HeatLevel {
    return level as HeatLevel;
  }

  /**
   * Called when an editor loses focus
   * Automatically refresh ingredients preview
   */
  onEditorBlur(): void {
    this.log.debug("ManageRecipeComponent: onEditorBlur - refreshing ingredients preview");
    this.previewIngredients();
  }

  /**
   * Build the recipe payload for API
   */
  private buildRecipePayload() {
    return {
      title: this.recipeTitle(),
      servings: this.servings(),
      prep_time_minutes: this.prepTime(),
      // A new recipe lands in the actor's default household (backend resolves
      // it when household_id is omitted); is_public toggles read-only discovery.
      is_public: this.isPublic(),
      steps: this.steps().map((s, index) => {
        const baseStep = {
          order: index + 1,
          type: s.type
        };

        if (s.type === 'instruction') {
          return {
            ...baseStep,
            text: s.text
          };
        } else if (s.type === 'cook') {
          // Only include fields relevant to the current device
          const baseCook: any = {
            ...baseStep,
            device: s.device,
            time_min: s.timeMin,
            text: s.text
          };

          if (s.device === 'oven') {
            return {
              ...baseCook,
              mode: s.mode,
              temp_c: s.tempC,
              preheat: s.preheat,
              rack_position: s.rackPosition
            };
          }

          if (s.device === 'pan') {
            return {
              ...baseCook,
              heat_level: s.heatLevel
            };
          }

          if (s.device === 'grill') {
            return {
              ...baseCook,
              heat_level: s.heatLevel,
              grill_mode: s.grillMode
            };
          }

          // pot/airfryer
          return {
            ...baseCook,
            temp_c: s.tempC
          };
        } else if (s.type === 'wait') {
          return {
            ...baseStep,
            time_min: s.waitTimeMin,
            location: s.waitLocation,
            covered: s.waitCovered,
            text: s.text
          };
        } else if (s.type === 'serve') {
          return {
            ...baseStep,
            text: s.text
          };
        }

        return baseStep;
      })
    };
  }

  /**
   * Preview ingredients list without saving
   * Calls: POST /api/recipes/ingredients-preview
   */
  previewIngredients(): void {
    const payload = this.buildRecipePayload() as RecipePreviewRequest;

    this.recipeService.previewIngredients(payload).subscribe({
      next: (response) => {
        this.aggregatedIngredients.set(response.aggregated);
      },
      error: () => {
        this.aggregatedIngredients.set([]);
      }
    });
  }

  /**
   * Validate the recipe form
   */
  validateForm(): boolean {
    const errors: { title?: string } = {};

    // Check if title is empty or only whitespace
    const title = this.recipeTitle().trim();
    if (!title || title.length === 0) {
      errors.title = 'Recipe title is required';
    }

    this.validationErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  // --- Classification facets (manual override) ---

  onCourseChange(key: string): void {
    this.selectedCourse.set(key ?? '');
    this.facetsTouched.set(true);
  }

  toggleSeason(key: string): void {
    const next = new Set(this.selectedSeasons());
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.selectedSeasons.set(next);
    this.facetsTouched.set(true);
  }

  toggleDishType(key: string): void {
    const next = new Set(this.selectedDishTypes());
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.selectedDishTypes.set(next);
    this.facetsTouched.set(true);
  }

  /**
   * Persist the manual facet override for the given recipe. Only called when the
   * user actually changed a facet control, so the auto-classifier is left alone
   * otherwise. Best-effort: a failure here is logged but does not block the save
   * (the recipe itself is already persisted).
   */
  private saveFacetsIfTouched(recipeId: number): void {
    if (!this.facetsTouched()) {
      return;
    }
    this.recipeFacetsService.updateRecipeFacets(recipeId, {
      course: this.selectedCourse(),
      seasons: Array.from(this.selectedSeasons()),
      dish_types: Array.from(this.selectedDishTypes()),
    }).subscribe({
      next: () => this.facetsTouched.set(false),
      error: (err) => this.log.error('Error saving recipe facets', err, {recipeId}),
    });
  }

  /**
   * Save the complete recipe
   * Create: POST /api/recipes
   * Edit:   PUT /api/recipes/:id
   */
  saveRecipe(): void {
    if (this.isSaving()) {
      return;
    }

    // Mark form as touched to show validation errors
    this.formTouched.set(true);

    // Validate the form
    if (!this.validateForm()) {
      return;
    }

    // Ensure we capture the latest editor content even if the user didn't blur.
    // (serializedTextChange is debounced and emitOnBlur may delay updates.)
    this.flushEditors(true);

    const payload = this.buildRecipePayload() as unknown as RecipeCreateRequest;
    this.isSaving.set(true);

    if (this.isEditMode() && this.editingRecipeId()) {
      const id = this.editingRecipeId()!;
      this.recipeService.updateRecipe(id, payload).subscribe({
        next: () => {
          this.saveFacetsIfTouched(id);
          this.isSaving.set(false);
          this.resetBaseline();
          this.router.navigate(['/recipe', id]);
        },
        error: () => {
          this.isSaving.set(false);
        }
      });
      return;
    }

    this.recipeService.createRecipe(payload).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        this.resetBaseline();
        if (res?.id) {
          this.saveFacetsIfTouched(res.id);
          this.router.navigate(['/recipe', res.id]);
        }
      },
      error: () => {
        this.isSaving.set(false);
      }
    });
  }

  /**
   * Back button handler. Just navigates to the right target; the unsaved-changes
   * confirmation is handled centrally by {@link canDeactivate} (the route guard),
   * which also covers the browser's back/forward buttons.
   */
  async onBackClick(): Promise<void> {
    const target = this.isEditMode() && this.editingRecipeId()
      ? ['/recipe', this.editingRecipeId()]
      : ['/recipes'];

    await this.router.navigate(target);
  }

  private stableJson(value: any): string {
    return JSON.stringify(value, (key, val) => {
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        return Object.keys(val)
          .sort()
          .reduce((acc: any, k) => {
            acc[k] = val[k];
            return acc;
          }, {});
      }
      return val;
    });
  }

  private currentPayloadJson(): string {
    // Only include fields that the backend cares about. Ingredients preview result is derived.
    return this.stableJson(this.buildRecipePayload());
  }

  private resetBaseline(): void {
    this.baselinePayloadJson = this.currentPayloadJson();
  }

  private hasUnsavedChanges(): boolean {
    if (this.isSaving() || this.isLoadingRecipe()) return false;
    if (!this.baselinePayloadJson) return false;
    // Pull any pending editor text into `steps` first — the editor only emits on
    // blur/debounce, so a step edited but not yet blurred would otherwise look clean.
    this.flushEditors();
    return this.currentPayloadJson() !== this.baselinePayloadJson;
  }

  /**
   * Force every recipe step editor to emit its current serialized text so that
   * `steps` reflects in-progress edits. Pass `emitBlur` to also trigger the
   * ingredient preview (as a real blur would).
   */
  private flushEditors(emitBlur = false): void {
    this.recipeEditors?.forEach((ed) => ed.flush({emitBlur}));
  }

  onIsPublicChange(next: boolean): void {
    this.isPublic.set(next);
  }

  addStepOfType(stepType: 'instruction' | 'cook' | 'wait' | 'serve'): void {
    if (stepType === 'instruction') {
      this.steps.update(steps => [
        ...steps,
        {
          id: this.nextStepId++,
          type: 'instruction',
          text: ''
        }
      ]);
    } else if (stepType === 'cook') {
      this.steps.update(steps => [
        ...steps,
        {
          id: this.nextStepId++,
          type: 'cook',
          device: 'oven',
          mode: 'top_bottom_heat',
          tempC: 180,
          timeMin: 30,
          preheat: false,
          rackPosition: 3,
          text: ''
        }
      ]);
    } else if (stepType === 'wait') {
      this.steps.update(steps => [
        ...steps,
        {
          id: this.nextStepId++,
          type: 'wait',
          waitTimeMin: 30,
          waitLocation: 'room',
          waitCovered: false,
          text: ''
        }
      ]);
    } else if (stepType === 'serve') {
      this.steps.update(steps => [
        ...steps,
        {
          id: this.nextStepId++,
          type: 'serve',
          text: ''
        }
      ]);
    }
  }

  removeStep(stepId: number): void {
    if (this.steps().length <= 1) {
      return; // Keep at least one step
    }
    this.steps.update(steps => steps.filter(s => s.id !== stepId));
  }

  // Drag and Drop Methods
  onDragStart(event: DragEvent, stepId: number): void {
    this.draggedStepId = stepId;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', stepId.toString());

      // Set the entire step container as the drag image
      const dragHandle = event.target as HTMLElement;
      const stepContainer = dragHandle.closest('.step-container') as HTMLElement;

      if (stepContainer) {
        // Create a clone for the drag image
        const clone = stepContainer.cloneNode(true) as HTMLElement;
        clone.style.position = 'absolute';
        clone.style.top = '-9999px';
        clone.style.left = '-9999px';
        clone.style.width = stepContainer.offsetWidth + 'px';
        clone.style.opacity = '0.8';
        clone.style.transform = 'none';
        clone.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
        document.body.appendChild(clone);

        // Calculate offset based on where user clicked relative to the step container
        const rect = stepContainer.getBoundingClientRect();
        const offsetX = event.clientX - rect.left;
        const offsetY = event.clientY - rect.top;

        event.dataTransfer.setDragImage(clone, offsetX, offsetY);

        // Remove the clone after drag starts
        setTimeout(() => {
          if (document.body.contains(clone)) {
            document.body.removeChild(clone);
          }
        }, 50);
      }
    }
  }

  onDragOver(event: DragEvent, stepId: number): void {
    event.preventDefault(); // Allow drop - CRITICAL for drop to work
    event.stopPropagation();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    // Don't reorder if hovering over the dragged item itself
    if (this.draggedStepId === null || this.draggedStepId === stepId) {
      return;
    }

    // Reorder in real-time as we hover
    const steps = this.steps();
    const draggedIndex = steps.findIndex(s => s.id === this.draggedStepId);
    const hoverIndex = steps.findIndex(s => s.id === stepId);

    if (draggedIndex === -1 || hoverIndex === -1 || draggedIndex === hoverIndex) {
      return;
    }

    // Reorder the steps array dynamically
    const newSteps = [...steps];
    const [draggedStep] = newSteps.splice(draggedIndex, 1);
    newSteps.splice(hoverIndex, 0, draggedStep);

    this.steps.set(newSteps);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Just finalize the drag - the reordering already happened in onDragOver
    this.draggedStepId = null;
    this.dragOverStepId = null;
  }

  onDragEnd(event: DragEvent): void {
    this.draggedStepId = null;
    this.dragOverStepId = null;
  }

  // Allow dropping anywhere in the steps wrapper area
  onWrapperDragOver(event: DragEvent): void {
    if (this.draggedStepId !== null) {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
    }
  }

  onWrapperDrop(event: DragEvent): void {
    if (this.draggedStepId !== null) {
      event.preventDefault();
      event.stopPropagation();
      // Just finalize - reordering happens in onDragOver
      this.draggedStepId = null;
      this.dragOverStepId = null;
    }
  }

  // Prevent drops on content elements (editors, inputs, etc.)
  onContentDragOver(event: DragEvent): void {
    // Only prevent default if we're currently dragging a step
    if (this.draggedStepId !== null) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onContentDrop(event: DragEvent): void {
    // Prevent editor from accepting drops during step reordering
    if (this.draggedStepId !== null) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  isDragging(stepId: number): boolean {
    return this.draggedStepId === stepId;
  }


  isAnyStepDragging(): boolean {
    return this.draggedStepId !== null;
  }

  onStepTextChange(stepId: number, text: string): void {
    this.steps.update(steps =>
      steps.map(s => s.id === stepId ? {...s, text} : s)
    );
  }

  onStepTextChangeReadable(stepId: number, textReadable: string): void {
    // We only store a single text field.
    this.steps.update(steps =>
      steps.map(s => s.id === stepId ? {...s, text: textReadable} : s)
    );
  }

  onDeviceChange(stepId: number, device: CookDevice): void {
    this.steps.update(steps =>
      steps.map(s => {
        if (s.id !== stepId || s.type !== 'cook') return s;

        // Normalize fields based on device to avoid leaking irrelevant props
        if (device === 'pan') {
          return {
            ...s,
            device,
            // pan uses heatLevel, not oven mode/tempC/preheat/rackPosition
            mode: undefined,
            tempC: undefined,
            preheat: undefined,
            rackPosition: undefined,
            grillMode: undefined,
            heatLevel: (s.heatLevel ?? 5) as HeatLevel
          };
        }

        if (device === 'oven') {
          return {
            ...s,
            device,
            mode: (s.mode ?? 'top_bottom_heat') as OvenMode,
            tempC: s.tempC ?? 180,
            preheat: s.preheat ?? false,
            rackPosition: (s.rackPosition ?? 3) as RackPosition,
            heatLevel: undefined,
            grillMode: undefined
          };
        }

        if (device === 'grill') {
          return {
            ...s,
            device,
            // grill uses heatLevel + direct/indirect mode, no oven mode/tempC/preheat/rackPosition
            mode: undefined,
            tempC: undefined,
            preheat: undefined,
            rackPosition: undefined,
            heatLevel: (s.heatLevel ?? 5) as HeatLevel,
            grillMode: (s.grillMode ?? 'direct') as GrillMode
          };
        }

        // Other cook devices: temperature + time, no oven mode/rack/preheat and no heatLevel
        return {
          ...s,
          device,
          mode: undefined,
          preheat: undefined,
          rackPosition: undefined,
          heatLevel: undefined,
          grillMode: undefined,
          tempC: s.tempC ?? 180
        };
      })
    );
  }

  onModeChange(stepId: number, mode: OvenMode): void {
    this.steps.update(steps =>
      steps.map(s => s.id === stepId ? {...s, mode} : s)
    );
  }

  onTempCChange(stepId: number, tempC: number): void {
    this.steps.update(steps =>
      steps.map(s => s.id === stepId ? {...s, tempC} : s)
    );
  }

  onHeatLevelChange(stepId: number, heatLevel: HeatLevel): void {
    this.steps.update(steps =>
      steps.map(s => s.id === stepId ? {...s, heatLevel} : s)
    );
  }

  onGrillModeChange(stepId: number, grillMode: GrillMode): void {
    this.steps.update(steps =>
      steps.map(s => s.id === stepId ? {...s, grillMode} : s)
    );
  }

  onTimeMinChange(stepId: number, timeMin: number): void {
    this.steps.update(steps =>
      steps.map(s => s.id === stepId ? {...s, timeMin} : s)
    );
  }

  onPreheatChange(stepId: number, preheat: boolean): void {
    this.steps.update(steps =>
      steps.map(s => s.id === stepId ? {...s, preheat} : s)
    );
  }

  onRackPositionChange(stepId: number, rackPosition: RackPosition): void {
    this.steps.update(steps =>
      steps.map(s => s.id === stepId ? {...s, rackPosition} : s)
    );
  }

  onWaitTimeMinChange(stepId: number, waitTimeMin: number): void {
    this.steps.update(steps =>
      steps.map(s => s.id === stepId ? {...s, waitTimeMin} : s)
    );
  }

  onWaitLocationChange(stepId: number, waitLocation: WaitLocation): void {
    this.steps.update(steps =>
      steps.map(s => s.id === stepId ? {...s, waitLocation} : s)
    );
  }

  onWaitCoveredChange(stepId: number, waitCovered: boolean): void {
    this.steps.update(steps =>
      steps.map(s => s.id === stepId ? {...s, waitCovered} : s)
    );
  }

  // Wait step text editor visibility methods
  showWaitStepTextEditor(stepId: number): void {
    this.waitStepsWithTextEditor.add(stepId);
  }

  isWaitStepTextEditorVisible(stepId: number, stepText?: string): boolean {
    // Show editor if:
    // 1. User has explicitly shown it, OR
    // 2. There's already text in the step (from backend)
    return this.waitStepsWithTextEditor.has(stepId) || !!(stepText && stepText.trim());
  }

  // Cook step text editor visibility methods
  showCookStepTextEditor(stepId: number): void {
    this.cookStepsWithTextEditor.add(stepId);
  }

  isCookStepTextEditorVisible(stepId: number, stepText?: string): boolean {
    // Show editor if:
    // 1. User has explicitly shown it, OR
    // 2. There's already text in the step (from backend)
    return this.cookStepsWithTextEditor.has(stepId) || !!(stepText && stepText.trim());
  }

  /**
   * Delete the recipe after confirmation
   */
  async deleteRecipe(): Promise<void> {
    if (this.isDeleting() || !this.isEditMode() || !this.editingRecipeId()) {
      return;
    }

    const confirmed = await this.confirm.ask({
      title: 'Delete recipe',
      message: `Are you sure you want to delete "${this.recipeTitle()}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmBtnClass: 'btn-danger'
    });

    if (!confirmed) {
      return;
    }

    this.isDeleting.set(true);
    const id = this.editingRecipeId()!;

    this.recipeService.deleteRecipe(id).subscribe({
      next: () => {
        this.isDeleting.set(false);
        // Reset baseline to avoid unsaved changes prompt
        this.baselinePayloadJson = null;
        // Navigate back to recipes list
        this.router.navigate(['/recipes']);
      },
      error: () => {
        this.isDeleting.set(false);
      }
    });
  }


}
